import React, { useEffect, useRef, useState } from 'react'
import {
  Container,
  Eyebrow,
  SectionTitle,
  Card,
} from '../AumhumSite/sections'
import { ArrowRight, MessageCircle, PhoneCall } from '../AumhumSite/icons'
import StepDots from './StepDots'
import {
  sendProspectOtp,
  verifyProspectOtp,
  signupPatientProspect,
} from 'src/consumer/services/patientProspectService'

// Step 3: mobile + OTP signup. Mobile is mandatory; email is never asked
// pre-login (per Aviral D2). WhatsApp OTP is the default channel; SMS
// fallback appears after the resend timer expires.
export default function MobileOtpStep({ resolvedPathway, onSignedUp, onBack }) {
  const [name, setName] = useState('')
  const [mobile, setMobile] = useState('')
  const [otp, setOtp] = useState('')
  const [channel, setChannel] = useState('whatsapp')
  const [sessionId, setSessionId] = useState(null)
  const [otpSent, setOtpSent] = useState(false)
  const [resendIn, setResendIn] = useState(0)
  const [consent, setConsent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const tickRef = useRef(null)

  useEffect(() => {
    if (resendIn <= 0) {
      if (tickRef.current) clearInterval(tickRef.current)
      tickRef.current = null
      return undefined
    }
    tickRef.current = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000)
    return () => {
      if (tickRef.current) clearInterval(tickRef.current)
    }
  }, [resendIn])

  const validMobile = (() => {
    const trimmed = (mobile || '').trim()
    if (!trimmed) return false
    // Permissive: must start with + and have 8+ digits. Replaced with the
    // proper libphonenumber check in Phase 2e when we hit the real backend.
    return /^\+\d{8,15}$/.test(trimmed.replace(/\s+/g, ''))
  })()

  const requestOtp = async (preferredChannel) => {
    setError('')
    if (!validMobile) {
      setError('Please enter your mobile in international format (e.g. +9198…).')
      return
    }
    try {
      setSubmitting(true)
      const res = await sendProspectOtp(mobile.trim(), preferredChannel)
      setSessionId(res.sessionId)
      setChannel(res.channel)
      setOtpSent(true)
      setResendIn(res.resendDelaySeconds || 20)
    } catch (e) {
      setError(e.message || 'Could not send the code. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const verifyAndSignup = async () => {
    setError('')
    if (!consent) {
      setError('Please agree to the privacy + terms before continuing.')
      return
    }
    try {
      setSubmitting(true)
      const verify = await verifyProspectOtp({ mobile: mobile.trim(), otp, sessionId, channel })
      const session = await signupPatientProspect({
        name: name.trim() || null,
        mobile: mobile.trim(),
        otpToken: verify.otpToken,
        pathway: resolvedPathway?.pathway,
        specialtyTag: resolvedPathway?.specialtyTag,
        subjectType: 'SELF',
        consents: { tos: true, privacy: true },
        source: 'WEB',
      })
      onSignedUp?.(session)
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Container className="pps-shell">
      <StepDots step={3} of={3} />
      <Eyebrow>Last step</Eyebrow>
      <SectionTitle as="h1">Create your free account</SectionTitle>

      {resolvedPathway ? (
        <div className="pps-recap">
          You picked <strong>{resolvedPathway.topLabel}
            {resolvedPathway.subLabel ? <> — {resolvedPathway.subLabel}</> : null}</strong>.
        </div>
      ) : null}

      <Card className="pps-card-form" emphasised>
        <div className="pps-field">
          <label className="pps-field-label" htmlFor="pps-name">Your name <span style={{ fontWeight: 400, color: 'var(--slate-500)' }}>(optional)</span></label>
          <input
            id="pps-name"
            className="pps-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="First name is fine"
            autoComplete="given-name"
          />
        </div>

        <div className="pps-field">
          <label className="pps-field-label" htmlFor="pps-mobile">Mobile number</label>
          <input
            id="pps-mobile"
            className="pps-input"
            type="tel"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            placeholder="+91 98xxxxxxxx"
            autoComplete="tel"
            disabled={otpSent && !error}
            inputMode="tel"
          />
          <span className="pps-help">
            We&rsquo;ll send a one-time code on WhatsApp. Standard rates may apply.
          </span>
        </div>

        {!otpSent ? (
          <div className="pps-cta-row">
            <button type="button" className="pps-btn is-ghost" onClick={onBack}>Back</button>
            <button
              type="button"
              className={`pps-btn is-primary${validMobile && !submitting ? '' : ' is-disabled'}`}
              disabled={!validMobile || submitting}
              onClick={() => requestOtp('whatsapp')}
            >
              {submitting ? 'Sending…' : (
                <>Send WhatsApp code <MessageCircle /></>
              )}
            </button>
          </div>
        ) : (
          <>
            <div className="pps-field">
              <label className="pps-field-label" htmlFor="pps-otp">
                Enter the 4–6 digit code we sent on {channel === 'sms' ? 'SMS' : 'WhatsApp'}
              </label>
              <div className="pps-otp-row">
                <input
                  id="pps-otp"
                  className="pps-input"
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="••••"
                  autoComplete="one-time-code"
                />
              </div>
              <div className="pps-channel-toggle">
                {resendIn > 0 ? (
                  <span>Resend in {resendIn}s</span>
                ) : (
                  <>
                    <button type="button" onClick={() => requestOtp(channel)}>Resend code</button>
                    {channel !== 'sms' ? (
                      <>
                        <span>·</span>
                        <button type="button" onClick={() => requestOtp('sms')}>
                          Send via SMS instead
                        </button>
                      </>
                    ) : null}
                  </>
                )}
              </div>
            </div>

            <div className="pps-checkbox-row">
              <input
                id="pps-consent"
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
              />
              <label htmlFor="pps-consent">
                I agree to AUMHUM&rsquo;s <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a> and{' '}
                <a href="/terms" target="_blank" rel="noreferrer">Terms</a>, and to share these
                details so the care team can find me a specialist.
              </label>
            </div>

            <div className="pps-cta-row">
              <button type="button" className="pps-btn is-ghost" onClick={onBack}>Back</button>
              <button
                type="button"
                className={`pps-btn is-primary${otp.length >= 4 && consent && !submitting ? '' : ' is-disabled'}`}
                disabled={otp.length < 4 || !consent || submitting}
                onClick={verifyAndSignup}
              >
                {submitting ? 'Creating…' : (<>Create account <ArrowRight /></>)}
              </button>
            </div>
          </>
        )}

        {error ? <div className="pps-error" role="alert">{error}</div> : null}

        <div className="pps-secondary-row">
          <a className="pps-btn is-ghost" href="https://wa.me/918047492952" target="_blank" rel="noreferrer">
            <PhoneCall /> Just want to talk? Chat on WhatsApp
          </a>
        </div>
      </Card>
    </Container>
  )
}
